/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { useMutation } from "@apollo/client"
import { WarningIcon } from "@chakra-ui/icons"
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Tag,
  TagLeftIcon,
  Text,
  useDisclosure,
  useToast
} from "@chakra-ui/react"
import { AnimatePresence, motion } from "framer-motion"
import gql from "graphql-tag"
import { useAtom } from "jotai"
import { useCallback, useEffect, useState, type FormEvent } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import {
  LOCAL_STORAGE_REGISTED_EMAIL,
  LOGGED_IN,
  Paths,
  REMEMBER_ME,
  RETURN_URL,
  SESSION_EXPIRED_KEY,
  TOKEN_NAME,
  USER_DETAILS_NAME,
  USER_NOT_VERIFIED,
  USER_NOT_VERIFIED_MESSAGE
} from "../../common/commonConstants"
import { userDetailsAtom } from "../../components/Menu/menu.store"
import { getSavedUserDetails } from "../../components/Menu/menu.support"
import { Heading3WhiteUnderlined } from "../../components/commons/Heading3WhiteUnderlined"
import { SnapLogo } from "../../components/commons/SnapLogo"
import { FormElement, formBoxStyleProps } from "../../components/commons/globalFormComponent"
import { iconCloseImg, whiteFireRightImg } from "../../components/images"
import { SnapBg } from "../../components/snapBg"
import { buttonStyles, errorMessageStyles } from "./login.styles"
import { ResetPassword } from "./resetPassword"
import { SignUpConfirmation } from "./signupNew/signup.confirmation"
import { SignUpMain } from "./signupNew/signup.main"
import { failForm } from "./signupNew/signup.support"

const LOGIN = gql`
  mutation LoginUser($username: String!, $password: String!) {
    loginUser(username: $username, password: $password) {
      token
    }
  }
`

interface LoginUserInterface {
  successCallBack?: () => void
  hideForgotPassword?: boolean
}

export const LoginForm = ({ successCallBack, hideForgotPassword }: LoginUserInterface): JSX.Element => {
  const [loginUser, { data, loading, error }] = useMutation(LOGIN)
  const [errorMessage, setErrorMessage] = useState<string>("")
  // Load remember me preference from localStorage on mount
  const [remeberMeCheck, setRemeberMeCheck] = useState<boolean>(() => {
    const savedPreference = localStorage.getItem(REMEMBER_ME)
    return savedPreference === "true"
  })
  const { isOpen, onOpen, onClose } = useDisclosure()
  const toast = useToast()

  useEffect(() => {
    const expired = sessionStorage.getItem(SESSION_EXPIRED_KEY)
    if (expired) {
      sessionStorage.removeItem(SESSION_EXPIRED_KEY)
      toast({
        title: "Session expired",
        description: "Your session has expired. Please log in again to continue.",
        status: "warning",
        duration: 7000,
        isClosable: true,
        position: "top"
      })
    }
  }, [toast])

  const handleLogin = (e: FormEvent): void => {
    setErrorMessage("")
    const form = e.target as HTMLFormElement
    const email = form.email.value
    const password = form.login_password.value
    if (email) {
      localStorage.setItem(LOCAL_STORAGE_REGISTED_EMAIL, email)
    }
    loginUser({ variables: { username: email, password } }).catch(() => {})

    e.preventDefault()
  }
  useEffect(() => {
    if (data) {
      const payloadToken = data.loginUser.token.split(".")[1]
      // decode base64
      const decoded = atob(payloadToken)
      localStorage.setItem(LOGGED_IN, "true")
      if (remeberMeCheck) {
        localStorage.setItem(TOKEN_NAME, data.loginUser.token)
        localStorage.setItem(USER_DETAILS_NAME, decoded)
        // Ensure remember me preference is saved
        localStorage.setItem(REMEMBER_ME, "true")
      } else {
        sessionStorage.setItem(TOKEN_NAME, data.loginUser.token)
        sessionStorage.setItem(USER_DETAILS_NAME, decoded)
        // Clear remember me preference if unchecked
        localStorage.removeItem(REMEMBER_ME)
      }

      if (successCallBack != null) {
        successCallBack()
      }
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      if (data?.error?.length) {
        failForm(data.error[0], toast)
      }
    }
  }, [data, remeberMeCheck, successCallBack, toast])

  const { isOpen: isVisible, onOpen: onOpenAlert } = useDisclosure({ defaultIsOpen: false })
  useEffect(() => {
    if (error && error.graphQLErrors.length > 0) {
      failForm(error.graphQLErrors[0] as unknown as string, toast)
    }
    if (error?.graphQLErrors?.[0].toString() === USER_NOT_VERIFIED) {
      onOpenAlert()
    }
  }, [error, toast, onOpenAlert])

  const [userName, setUserName] = useState<string>("")
  const [password, setPassword] = useState<string>("")

  return (
    <AnimatePresence mode="popLayout">
      <>
        {isVisible && (
          <motion.div
            exit={{ opacity: 0, height: "0" }}
            animate={{ opacity: 1, height: "200px" }}
            initial={{ opacity: 0, height: "400px" }}
            key="alert-warning"
          >
            <Box w="100%">
              <Box bg="orange.100" p="4">
                <Heading fontSize={"22px"} mb="4" color="orange.900">
                  Unverified User!
                </Heading>
                <Text color="orange.800">{USER_NOT_VERIFIED_MESSAGE}</Text>
                <Button
                  variant="secondary"
                  mt="4"
                  onClick={() => {
                    window.location.href = `${window.location.protocol}//${window.location.host}${Paths.SIGNUP_CONFIRMATION}`
                  }}
                >
                  Take me there
                </Button>
              </Box>
            </Box>
          </motion.div>
        )}
        {!isVisible && (
          <motion.div
            exit={{ opacity: 0, height: "0" }}
            animate={{ opacity: 1, height: "500px" }}
            initial={{ opacity: 0, height: "500px" }}
            key="login-form"
          >
            <Flex {...formBoxStyleProps} pt="32px" w={["280px", "280px", "365px", "365px"]}>
              <form
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "column"
                }}
                onSubmit={handleLogin}
                method="post"
              >
                <FormElement
                  data-testid="form-input-email"
                  name="email"
                  type="email"
                  placeHolder="Your Email"
                  label="Email Address"
                  requiredText=""
                  htmlValidation={{ required: true }}
                  formControlProps={{ mt: "0", ml: "16px", mb: "16px" }}
                  onChangeCallBack={value => {
                    setUserName(value)
                  }}
                  value={userName}
                />
                <FormElement
                  data-testid="form-input-password"
                  name="login_password"
                  type="password"
                  placeHolder="Your Password"
                  label="Password"
                  htmlValidation={{ required: true }}
                  onChangeCallBack={value => {
                    setPassword(value)
                  }}
                  formControlProps={{ mb: 0, ml: "16px" }}
                  value={password}
                  loginPage={true}
                />
                {!hideForgotPassword && (
                  <Button
                    variant={"RBodyBold"}
                    {...buttonStyles}
                    color="#ffffff !important"
                    mt={["8px", "8px", "8px"]}
                    lineHeight="24px"
                    bg="transparent"
                    borderRadius="0"
                    ml="12px"
                    onClick={onOpen}
                  >
                    Forgot Your Password?
                  </Button>
                )}
                <Box w="100%" px="2" display={"flex"} justifyContent={"flex-start"} mt={hideForgotPassword ? "4" : "0"}>
                  <Checkbox
                    pl="4px"
                    display={"flex"}
                    alignItems={"flex-start"}
                    variant="Body1Regular"
                    color="white"
                    isChecked={remeberMeCheck}
                    iconSize="0.875rem"
                    onChange={() => {
                      const newValue = !remeberMeCheck
                      setRemeberMeCheck(newValue)
                      // Save remember me preference to localStorage
                      if (newValue) {
                        localStorage.setItem(REMEMBER_ME, "true")
                      } else {
                        localStorage.removeItem(REMEMBER_ME)
                      }
                    }}
                    spacing="0.75rem"
                  >
                    <span
                      style={{
                        textAlign: "left",
                        fontFamily: "PT Sans",
                        fontSize: "15px",
                        fontWeight: "400",
                        lineHeight: "19px",
                        position: "relative",
                        top: "-3px",
                        left: "-2px",
                        width: "235px",
                        display: "inline-block"
                      }}
                    >
                      Remember my details
                    </span>
                  </Checkbox>
                </Box>

                <Button
                  display="block"
                  mt="32px"
                  mb={["32px", "32px", "64px", "64px"]}
                  mx="auto"
                  w="100px"
                  px="25px"
                  py="5px"
                  variant="Button1RegularOutline"
                  lineHeight={"20px"}
                  type="submit"
                  isLoading={loading}
                  name="login"
                >
                  Log In
                </Button>
              </form>
              {errorMessage && (
                <Tag {...errorMessageStyles}>
                  <TagLeftIcon boxSize="12px" as={WarningIcon} />
                  {errorMessage}
                </Tag>
              )}
            </Flex>
            <Modal isOpen={isOpen} onClose={onClose} size="full">
              <ModalOverlay />
              <ModalContent>
                <ModalCloseButton color="white" />
                <ModalBody bg={"primary"} padding="0">
                  <ResetPassword
                    onBack={() => {
                      onClose()
                    }}
                  />
                </ModalBody>
              </ModalContent>
            </Modal>
          </motion.div>
        )}
      </>
    </AnimatePresence>
  )
}
interface loginProps {
  successCallBack?: () => void
  loginCancelCallBack?: () => void
}
export const Login = ({ successCallBack, loginCancelCallBack }: loginProps): JSX.Element => {
  const [showEmailVerification, setShowEmailVerification] = useState<boolean>(false)
  const navigate = useNavigate()
  const location = useLocation()
  const signupOnly = new URLSearchParams(location.search).get("signup") === "true"
  const [hideLoginForm, setHideLoginForm] = useState<boolean>(signupOnly)
  const [userDetails, setUserDetails] = useAtom(userDetailsAtom)

  const checkAndGetSavedUserDetails = useCallback((): void => {
    const savedUserDetails = getSavedUserDetails()
    if (savedUserDetails) {
      setUserDetails(savedUserDetails)
    }
  }, [setUserDetails])

  useEffect(() => {
    setHideLoginForm(signupOnly)
    checkAndGetSavedUserDetails()
  }, [])

  useEffect(() => {
    if (userDetails) {
      successCallBack?.()
    }
  }, [userDetails])
  // onload check if login data is prenset.
  const handleLoginSuccess = (): void => {
    if (successCallBack) {
      checkAndGetSavedUserDetails()
      successCallBack()
    }
    if (!successCallBack) {
      // Check if there's a return URL stored (from auth error redirect)
      const returnUrl = sessionStorage.getItem(RETURN_URL)
      if (returnUrl) {
        // Clear the return URL and navigate to it
        sessionStorage.removeItem(RETURN_URL)
        navigate(returnUrl)
      } else {
        navigate("/")
      }
    }
  }

  return (
    <Box pos={"fixed"} top="0" w="100vw" height="100vh" zIndex="900" overflow="auto" transition={"0.5s ease-in-out"}>
      {!showEmailVerification && (
        <SnapBg
          chakraProps={{
            backgroundImage: ["none", "none", `url(${whiteFireRightImg})`],
            bgRepeat: "no-repeat",
            backgroundPosition: "100% 100%",
            bg: "primary"
          }}
        >
          <Box display="flex" justifyContent="flex-end" position="fixed" top="env(safe-area-inset-top)" right="0" zIndex={100}>
            <Button
              display="flex"
              justifySelf="flex-end"
              variant="ghost"
              onClick={() => {
                navigate("/")
                if (loginCancelCallBack) {
                  loginCancelCallBack()
                }
              }}
            >
              <Image src={iconCloseImg} />
            </Button>
          </Box>
          <Box
            w={showEmailVerification ? "70%" : ["280px", "280px", "365px"]}
            margin={"auto"}
            pt={["24px", "24px", "100px", "100px"]}
            px={["16px", "16px", 0, 0]}
          >
            <Flex align={"center"} justify="center" flexDir={"column"}>
              <>
                <SnapLogo
                  props={{
                    width: ["180px", "180px", "180px", "239px", "239px", "239px"],
                    height: ["126px", "126px", "126px", "167px", "167px", "167px"]
                  }}
                />

                <Box maxHeight={hideLoginForm ? "0" : "500"} transition="550ms" overflow={"hidden"} mt={["32px", "32px", "8px", "8px"]}>
                  {!hideLoginForm && (
                    <>
                      <Heading3WhiteUnderlined text="To continue, please log in to Snap" props={{ color: "white", p: [0, 0, "10px", "10px"] }} />
                      <LoginForm successCallBack={handleLoginSuccess} />
                    </>
                  )}
                </Box>
                <Box>
                  <SignUpMain
                    headingText={signupOnly ? "Create an account with us" : undefined}
                    successCallBack={() => {
                      setShowEmailVerification(true)
                    }}
                    showOptionalValuesCallBack={() => {
                      setHideLoginForm(true)
                    }}
                    onBackCallback={() => {
                      if (loginCancelCallBack) {
                        loginCancelCallBack()
                      }
                    }}
                  />
                </Box>
              </>
            </Flex>
          </Box>
        </SnapBg>
      )}
      {showEmailVerification && <SignUpConfirmation />}
    </Box>
  )
}
